import { RequestRoot } from 'types';
import { fill, Mapping, setString, setNullableString, setMaskedDate, setEnum, DATE_FULL, fillObject, fillArray, DATE_YYYY, DATE_YYYY_MM, setNullableMaskedDate, setBoolean, Features } from 'controls'
import { RequestFeatures, InputMedia, PersonAddress, PersonIdentity, ActiveFPassportInfo, RequestCause, RequestGoal, Gender, ApplicantIdentityDocumentType, RepresentativeIdentityDocumentType } from './features';
import { ActiveFPassportInfoV2, ExtendedRestrictionV2, IndependentChildDocumentTypeV2, PersonAddressV2, RepresentativeProofDocumentTypeV2, RequestCauseV2, RequestFeaturesV2, RestrictionV2, TempAddressTypeV2 } from './features2';

export function importFromManual(result: RequestRoot): void {
  if (result.features instanceof RequestFeatures) {
    const features = result.features as RequestFeatures;
    features.ambulanceOptions.departmentLong = result.uid!.substring(0, 9);
    features.stationaryOptions.departmentLong = result.uid!.substring(0, 9);
    features.inputMedia = InputMedia.Manual;
    features.applicantDate = Features.today(new Date());
    features.receptionDate = Features.today(new Date());
  }
  if (result.features instanceof RequestFeaturesV2) {
    const features = result.features as RequestFeaturesV2;
    features.ambulanceOptions.departmentLong = result.uid!.substring(0, 9);
    features.stationaryOptions.departmentLong = result.uid!.substring(0, 9);
    features.inputMedia = InputMedia.Manual;
    features.applicantDate = Features.today(new Date());
    features.receptionDate = Features.today(new Date());
  }
}

function importFrom552QrCodeToV1(target: RequestFeatures, source: any) {
  let addressMapping: Mapping<PersonAddress> = {
    "1": setString(t => t.index),
    "2": setString(t => t.country),
    "3": setNullableString(t => t.admDivision),
    "4": setString(t => t.settlement),
    "5": setNullableString(t => t.street),
    "6": setNullableString(t => t.building),
    "7": setNullableString(t => t.housing),
    "8": setNullableString(t => t.structure),
    "9": setNullableString(t => t.flat)
  };
  let activeFPassportMapping: Mapping<ActiveFPassportInfo> = {
    "2": setString(t => t.series),
    "3": setString(t => t.number),
    "4": setMaskedDate(t => t.issueDate, DATE_FULL),
    "6": setString(t => t.issuer),
    "12": setBoolean(t => t.mustBeCanceled)
  };
  let personIdentityMapping: Mapping<PersonIdentity> = {
    "1": setString(t => t.fullName.firstName),
    "2": setNullableString(t => t.fullName.middleName),
    "3": setString(t => t.fullName.lastName),
    "11": setMaskedDate(t => t.birthDate, DATE_FULL),
    "26": (t, s) => { if (s === 1 && t.birthDate.value) { t.birthDate.value = t.birthDate.value.substr(0, 4) + '-00-00'; } }
  };
  fill(target, source, {
    "4": (t, s) => {
      fill(t, s, {
        "1": setEnum(t => t.cause, {
          1: RequestCause.FirstTime,
          2: RequestCause.SecondPassport,
          3: RequestCause.InsteadOfUsedPassport,
          4: RequestCause.InsteadOfSpoiltPassport,
          5: RequestCause.InsteadOfLostPassport
        }, "CommandId"),
        "2": setEnum(t => t.goal, { 0: RequestGoal.LivingAbroad, 1: RequestGoal.TemporaryTrips }),
        "3": (t, s) => {
          fill(t.applicant.personIdentity, s, personIdentityMapping);
          fill(t.applicant, s, {
            "4": setString(t => t.firstNameLat),
            "5": setString(t => t.lastNameLat),
            "8": setNullableString(t => t.secondNationality),
            "9": setString(t => t.birthPlaceRus),
            "10": setString(t => t.birthPlaceLat),
            "12": setEnum(t => t.gender, { 0: Gender.Male, 1: Gender.Female }),
            "32": fillObject(t => t.permAddress, addressMapping),
            "14": setString(t => t.permAddress.phone),
            "33": fillObject(t => t.tempAddress, addressMapping),
            "16": setString(t => t.tempAddress.phone),
            "17": fillObject(t => t, {
              "0": fillObject(t => t.activeFPassports.p1, activeFPassportMapping),
              "1": fillObject(t => t.activeFPassports.p2, activeFPassportMapping)
            }),
            "18": fillArray(t => t.nameChanges, fillObject(nc => nc, {
              "1": setString(t => t.firstName),
              "2": setNullableString(t => t.middleName),
              "3": setString(t => t.lastName),
              "4": setMaskedDate(t => t.year, DATE_YYYY),
              "5": setString(t => t.location)
            })),
            "19": fillObject(t => t.identityDocument, {
              "Series": setString(t => t.series),
              "Number": setString(t => t.number),
              "Issuer": setString(t => t.issuer),
              "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
              "Type": setEnum(t => t.type, {
                1: ApplicantIdentityDocumentType.BirthCertificate,
                3: ApplicantIdentityDocumentType.RussianPassport,
                5: ApplicantIdentityDocumentType.NoDocument
              })
            }),
            "20": fillArray(t => t.jobRecords, fillObject(jr => jr, {
              "1": setString(t => t.position),
              "2": setString(t => t.address),
              "3": setMaskedDate(t => t.from, DATE_YYYY_MM),
              "4": setNullableMaskedDate(t => t.to, DATE_YYYY_MM)
            })),
            "25": setString(t => t.birthPlaceFull)
          });
          fill(t, s, {
            "24": fillObject(t => t.citizenshipCertificate, {
              "1": (t, s) => t.active = !!s,
              "2": setString(t => t.number),
              "3": setMaskedDate(t => t.date, DATE_FULL),
              "4": setString(t => t.officerPost),
              "5": setString(t => t.officerName)
            })
          });
        },
        "4": (t, s) => {
          fill(t.representative.personIdentity, s, personIdentityMapping);
          fill(t.representative, s, {
            "9": setString(t => t.birthPlace),
            "12": setEnum(t => t.gender, { 0: Gender.Male, 1: Gender.Female }),
            "13": setString(t => t.address.text),
            "18": fillArray(t => t.nameChanges, fillObject(nc => nc, {
              "1": setString(t => t.firstName),
              "2": setNullableString(t => t.middleName),
              "3": setString(t => t.lastName),
              "4": setMaskedDate(t => t.year, DATE_YYYY),
              "5": setString(t => t.location)
            })),
            "19": fillObject(t => t.identityDocument, {
              "Series": setString(t => t.series),
              "Number": setString(t => t.number),
              "Issuer": setString(t => t.issuer),
              "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
              "Type": setEnum(t => t.type, {
                0: RepresentativeIdentityDocumentType.Custom,
                3: RepresentativeIdentityDocumentType.RussianPassport,
                4: RepresentativeIdentityDocumentType.RussianForeignPassport
              })
            })
          })
        },
        "6": setNullableString(t => t.secretAccess),
        "7": setNullableString(t => t.departureRestrictions),
        "8": setNullableString(t => t.militaryService),
        "9": setNullableString(t => t.conviction),
        "10": setNullableString(t => t.legalObligations),
        "11": setString(t => t.residenceCountry),
        "12": setMaskedDate(t => t.applicant.jobManDate, DATE_FULL),
        "13": setString(t => t.applicant.jobManName),
        "SourceInfo": fillObject(t => t, {
          "WebSiteVersion": setString(t => t.versionSite)
        })
      });
    },
    "11": fillObject(t => t.mailing, {
      "1": (t, s) => t.active = s,
      "5": setString(t => t.addressRus),
      "6": setString(t => t.addressLat)
    })
  });
  if (target.applicant.activeFPassports.p2.series) {
    target.applicant.activeFPassports.p1.next = true;
  }
}

function importFrom60QrCodeToV1(target: RequestFeatures, source: any) {

}

function importFrom552QrCodeToV2(target: RequestFeaturesV2, source: any) {
  let addressMapping: Mapping<PersonAddressV2> = {
    "2": setString(t => t.country),
    "3": setNullableString(t => t.admDivision),
    "4": setString(t => t.settlement),
    "5": setNullableString(t => t.street),
    "6": setNullableString(t => t.building),
    "7": setNullableString(t => t.housing),
    "8": setNullableString(t => t.structure),
    "9": setNullableString(t => t.flat)
  };
  let activeFPassportMapping: Mapping<ActiveFPassportInfoV2> = {
    "2": setString(t => t.series),
    "3": setString(t => t.number),
    "4": setMaskedDate(t => t.issueDate, DATE_FULL),
    "6": setString(t => t.issuer),
    "12": setBoolean(t => t.mustBeCanceled)
  };
  let personIdentityMapping: Mapping<PersonIdentity> = {
    "1": setString(t => t.fullName.firstName),
    "2": setNullableString(t => t.fullName.middleName),
    "3": setString(t => t.fullName.lastName),
    "11": setMaskedDate(t => t.birthDate, DATE_FULL),
    "26": (t, s) => { if (s === 1 && t.birthDate.value) { t.birthDate.value = t.birthDate.value.substr(0, 4) + '-00-00'; } }
  };
  fill(target, source, {
    "4": (t, s) => {
      fill(t, s, {
        "1": setEnum(t => t.cause, {
          1: RequestCauseV2.FirstTime,
          2: RequestCauseV2.AdditionToActive,
          3: RequestCauseV2.InsteadOfUsedOrSpoiltPassport,
          4: RequestCauseV2.InsteadOfUsedOrSpoiltPassport,
          5: RequestCauseV2.InsteadOfLostPassport
        }, "CommandId"),
        "3": (t, s) => {
          fill(t.applicant.personIdentity, s, personIdentityMapping);
          fill(t.applicant, s, {
            "4": setString(t => t.firstNameLat),
            "5": setString(t => t.lastNameLat),
            "9": setString(t => t.birthPlaceRus),
            "10": setString(t => t.birthPlaceLat),
            "12": setEnum(t => t.gender, { 0: Gender.Male, 1: Gender.Female }),
            "32": fillObject(t => t.permAddress.address, addressMapping),
            "33": fillObject(t => t.tempAddress.address, addressMapping),
            "17": fillObject(t => t, {
              "0": fillObject(t => t.activeFPassports.p1, activeFPassportMapping),
              "1": fillObject(t => t.activeFPassports.p2, activeFPassportMapping)
            }),
            "18": fillArray(t => t.nameChanges, fillObject(nc => nc, {
              "1": setString(t => t.firstName),
              "2": setNullableString(t => t.middleName),
              "3": setString(t => t.lastName),
              "4": setMaskedDate(t => t.year, DATE_YYYY),
              "5": setString(t => t.location)
            })),
            "19": fillObject(t => t.identityDocument, {
              "Series": setString(t => t.series),
              "Number": setString(t => t.number),
              "Issuer": setString(t => t.issuer),
              "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
              "Type": setEnum(t => t.type, {
                1: ApplicantIdentityDocumentType.BirthCertificate,
                3: ApplicantIdentityDocumentType.RussianPassport,
                5: ApplicantIdentityDocumentType.NoDocument
              })
            }),
            "20": fillArray(t => t.jobRecords, fillObject(jr => jr, {
              "1": setString(t => t.position),
              "2": setString(t => t.address),
              "3": setMaskedDate(t => t.from, DATE_YYYY_MM),
              "4": setNullableMaskedDate(t => t.to, DATE_YYYY_MM)
            })),
            "25": setString(t => t.birthPlaceFull)
          });
          fill(t, s, {
            "24": fillObject(t => t.citizenshipCertificate, {
              "1": (t, s) => t.active = !!s,
              "2": setString(t => t.number),
              "3": setMaskedDate(t => t.date, DATE_FULL),
              "4": setString(t => t.officerPost),
              "5": setString(t => t.officerName)
            })
          });
        },
        "4": (t, s) => {
          fill(t.representative.personIdentity, s, personIdentityMapping);
          fill(t.representative, s, {
            "9": setString(t => t.birthPlace),
            "12": setEnum(t => t.gender, { 0: Gender.Male, 1: Gender.Female }),
            "19": fillObject(t => t.identityDocument, {
              "Series": setString(t => t.series),
              "Number": setString(t => t.number),
              "Issuer": setString(t => t.issuer),
              "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
              "Type": setEnum(t => t.type, {
                0: RepresentativeIdentityDocumentType.Custom,
                3: RepresentativeIdentityDocumentType.RussianPassport,
                4: RepresentativeIdentityDocumentType.RussianForeignPassport
              })
            })
          })
        },
        "6": (t, s) => {
          if (s) {
            t.secretAccess.value = RestrictionV2.Restricted;
            t.secretAccess.organization = String(s);
          } else {
            t.secretAccess.value = RestrictionV2.NotRestricted;
          }
        },
        "7": (t, s) => {
          if (s) {
            t.contractObligations.value = RestrictionV2.Restricted;
            t.contractObligations.organization = String(s);
          } else {
            t.contractObligations.value = RestrictionV2.NotRestricted;
          }
        },
        "12": setMaskedDate(t => t.applicant.jobManDate, DATE_FULL),
        "13": setString(t => t.applicant.jobManName),
        "SourceInfo": fillObject(t => t, {
          "WebSiteVersion": setString(t => t.versionSite)
        })
      });
    },
    "11": fillObject(t => t.mailing, {
      "1": (t, s) => t.active = s,
      "5": setString(t => t.addressRus),
      "6": setString(t => t.addressLat)
    })
  });
  if (target.applicant.activeFPassports.p2.series) {
    target.applicant.activeFPassports.p1.next = true;
  }
}

function importFrom60QrCodeToV2(target: RequestFeaturesV2, source: any) {
  fill(target, source, {
    "4": (t, s) => fill(t, s, {
      "1": setEnum(t => t.cause, { 0: RequestCauseV2.FirstTime, 8: RequestCauseV2.AdditionToActive, 7: RequestCauseV2.InsteadOfActive, 3: RequestCauseV2.InsteadOfLostPassport, 6: RequestCauseV2.InsteadOfUsedOrSpoiltPassport }, "CommandId"),
      "3": (t, s) => {
        fill(t.applicant.personIdentity, s, {
          "1": setString(t => t.fullName.firstName),
          "2": setNullableString(t => t.fullName.middleName),
          "3": setString(t => t.fullName.lastName),
          "11": setMaskedDate(t => t.birthDate, DATE_FULL),
          "26": (t, s) => { if (s === 1 && t.birthDate.value) { t.birthDate.value = t.birthDate.value.substr(0, 4) + '-00-00'; } }
        });
        fill(t, s, {
          "4": setString(t => t.applicant.firstNameLat),
          "5": setString(t => t.applicant.lastNameLat),
          "9": setString(t => t.applicant.birthPlaceRus),
          "10": setString(t => t.applicant.birthPlaceLat),
          "12": setEnum(t => t.applicant.gender, { 0: Gender.Male, 1: Gender.Female }),
          "14": setString(t => t.applicant.phone),
          "17": fillObject(t => t, {
            "0": fillObject(t => t.applicant.activeFPassports.p1, {
              "2": setString(t => t.series),
              "3": setString(t => t.number),
              "4": setMaskedDate(t => t.issueDate, DATE_FULL),
              "6": setString(t => t.issuer),
              "12": setBoolean(t => t.mustBeCanceled),
              "13": setBoolean(t => t.lost)

            }),
            "1": fillObject(t => t.applicant.activeFPassports.p2, {
              "2": setString(t => t.series),
              "3": setString(t => t.number),
              "4": setMaskedDate(t => t.issueDate, DATE_FULL),
              "6": setString(t => t.issuer),
              "12": setBoolean(t => t.mustBeCanceled),
              "13": setBoolean(t => t.lost)
            })
          }),
          "18": fillArray(t => t.applicant.nameChanges, fillObject(nc => nc, {
            "1": setString(t => t.firstName),
            "2": setNullableString(t => t.middleName),
            "3": setString(t => t.lastName),
            "4": setMaskedDate(t => t.year, DATE_FULL),
            "5": setString(t => t.location)
          })),
          "19": fillObject(t => t.applicant.identityDocument, {
            "Series": setString(t => t.series),
            "Number": setString(t => t.number),
            "Issuer": setString(t => t.issuer),
            "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
            "Type": setEnum(t => t.type, {
              1: ApplicantIdentityDocumentType.BirthCertificate,
              3: ApplicantIdentityDocumentType.RussianPassport,
              5: ApplicantIdentityDocumentType.NoDocument
            })
          }),
          "20": fillArray(t => t.applicant.jobRecords, fillObject(jr => jr, {
            "1": setString(t => t.position),
            "2": setString(t => t.address),
            "3": setMaskedDate(t => t.from, DATE_YYYY_MM),
            "4": setNullableMaskedDate(t => t.to, DATE_YYYY_MM)
          })),
          "25": setString(t => t.applicant.birthPlaceFull),
          "27": setString(t => t.applicant.email),
          "30": fillObject(t => t.applicant.tempAddress, {
            "1": fillObject(t => t.address, {
              "1": setString(t => t.country),
              "2": setNullableString(t => t.admDivision),
              "3": setNullableString(t => t.region),
              "4": setString(t => t.settlement),
              "5": setNullableString(t => t.street),
              "6": setNullableString(t => t.building),
              "7": setNullableString(t => t.housing),
              "8": setNullableString(t => t.structure),
              "9": setNullableString(t => t.flat)
            }),
            "2": (t, s) => s ? t.type = TempAddressTypeV2.NotRegistered_FAKTICHESKOE_PROZHIVANIE : t.type = TempAddressTypeV2.Registered_PREBYVANIE,
            "3": fillObject(t => t, {
              "1": setMaskedDate(t => t.from, DATE_FULL),
              "2": setMaskedDate(t => t.to, DATE_FULL)
            })
          }),
          "31": fillObject(t => t.applicant.permAddress, {
            "1": fillObject(t => t.address, {
              "1": setString(t => t.country),
              "2": setNullableString(t => t.admDivision),
              "3": setNullableString(t => t.region),
              "4": setString(t => t.settlement),
              "5": setNullableString(t => t.street),
              "6": setNullableString(t => t.building),
              "7": setNullableString(t => t.housing),
              "8": setNullableString(t => t.structure),
              "9": setNullableString(t => t.flat)
            }),
            "2": setMaskedDate(t => t.from, DATE_FULL)
          })
        })
      },
      "4": (t, s) => {
        fill(t.representative.personIdentity, s, {
          "1": setString(t => t.fullName.firstName),
          "2": setNullableString(t => t.fullName.middleName),
          "3": setString(t => t.fullName.lastName),
          "11": setMaskedDate(t => t.birthDate, DATE_FULL),
          "26": (t, s) => { if (s === 1 && t.birthDate.value) { t.birthDate.value = t.birthDate.value.substr(0, 4) + '-00-00'; } }
        });
        fill(t, s, {
          "12": setEnum(t => t.representative.gender, { 0: Gender.Male, 1: Gender.Female }),
          "14": setString(t => t.representative.phone),
          "19": fillObject(t => t.representative.identityDocument, {
            "Series": setString(t => t.series),
            "Number": setString(t => t.number),
            "Issuer": setString(t => t.issuer),
            "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
            "Type": setEnum(t => t.type, {
              0: RepresentativeIdentityDocumentType.Custom,
              3: RepresentativeIdentityDocumentType.RussianPassport,
              4: RepresentativeIdentityDocumentType.RussianForeignPassport
            })
          }),
          "27": setString(t => t.representative.email),
          "9": setString(t => t.representative.birthPlace),
          "28": fillObject(t => t.representative.representativeProof, {
            "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
            "Issuer": setString(t => t.issuer),
            "Number": setString(t => t.number),
            "Series": setString(t => t.series),
            "Type": setEnum(t => t.type, { 1: RepresentativeProofDocumentTypeV2.BirthCertificate, 6: RepresentativeProofDocumentTypeV2.Trustee })
          }),
          "29": fillObject(t => t.representative.trustRecipient, {
            "1": fillObject(t => t.personIdentity, {
              "1": fillObject(t => t.fullName, {
                "1": setString(t => t.lastName),
                "2": setString(t => t.firstName),
                "3": setNullableString(t => t.middleName)
              }),
              "2": setNullableMaskedDate(t => t.birthDate, DATE_FULL),
              "3": (t, s) => { if (String(s).startsWith('00.00.') && t.birthDate.value) { t.birthDate.value = t.birthDate.value.substr(0, 4) + '-00-00'; } }
            }),
            "2": (t, s) => t.gender = s === 0 ? Gender.Male : Gender.Female,
            "3": setString(t => t.birthPlace)
          }),
          "30": fillObject(t => t.representative.tempAddress, {
            "1": fillObject(t => t.address, {
              "1": setString(t => t.country),
              "2": setNullableString(t => t.admDivision),
              "3": setNullableString(t => t.region),
              "4": setString(t => t.settlement),
              "5": setNullableString(t => t.street),
              "6": setNullableString(t => t.building),
              "7": setNullableString(t => t.housing),
              "8": setNullableString(t => t.structure),
              "9": setNullableString(t => t.flat)
            }),
            "2": (t, s) => s ? t.type = TempAddressTypeV2.NotRegistered_FAKTICHESKOE_PROZHIVANIE : t.type = TempAddressTypeV2.Registered_PREBYVANIE,
            "3": fillObject(t => t, {
              "1": setMaskedDate(t => t.from, DATE_FULL),
              "2": setMaskedDate(t => t.to, DATE_FULL)
            })
          }),
          "31": fillObject(t => t.representative.permAddress, {
            "1": fillObject(t => t.address, {
              "1": setString(t => t.country),
              "2": setNullableString(t => t.admDivision),
              "3": setNullableString(t => t.region),
              "4": setString(t => t.settlement),
              "5": setNullableString(t => t.street),
              "6": setNullableString(t => t.building),
              "7": setNullableString(t => t.housing),
              "8": setNullableString(t => t.structure),
              "9": setNullableString(t => t.flat)
            }),
            "2": setMaskedDate(t => t.from, DATE_FULL)
          })
        })
      },
      "12": setMaskedDate(t => t.applicant.jobManDate, DATE_FULL),
      "13": setString(t => t.applicant.jobManName),
      "15": setMaskedDate(t => t.applicantDate, DATE_FULL),
      "SourceInfo": fillObject(t => t, {
        "WebSiteVersion": setString(t => t.versionSite)
      }),
      "21": fillObject(t => t, {
        "1": fillObject(t => t.secretAccess, {
          "1": setString(t => t.organization),
          "2": setMaskedDate(t => t.year, DATE_YYYY)
        }),
        "2": fillObject(t => t.contractObligations, {
          "1": setString(t => t.organization),
          "2": setMaskedDate(t => t.year, DATE_YYYY)
        }),
        "3": (t, s) => t.militaryService = s ? RestrictionV2.Restricted : RestrictionV2.NotRestricted,
        "4": (t, s) => t.convicted = s ? RestrictionV2.Restricted : RestrictionV2.NotRestricted,
        "5": (t, s) => t.accused = s ? RestrictionV2.Restricted : RestrictionV2.NotRestricted,
        "6": (t, s) => t.legalObligations = s ? RestrictionV2.Restricted : RestrictionV2.NotRestricted,
        "7": (t, s) => t.bankrupt = s ? RestrictionV2.Restricted : RestrictionV2.NotRestricted
      }),
      "22": fillObject(t => t.independentChildDocument, {
        "IssueDate": setMaskedDate(t => t.issueDate, DATE_FULL),
        "Issuer": setString(t => t.issuer),
        "Number": setString(t => t.number),
        "Series": setString(t => t.series),
        "Type": (t, s) => { if (s === 7) t.type = IndependentChildDocumentTypeV2.SelfSufficientCourtDecision; if (s === 8) t.type = IndependentChildDocumentTypeV2.SelfSufficientMarriageCertificate; if (s === 9) t.type = IndependentChildDocumentTypeV2.SelfSufficientGuardianDecision; }
      })
    }),
    "3": fillObject(t => t, {
      "Code": setString(t => t.mobileOptions.department)
    }),
    "11": fillObject(t => t.mailing, {
      "1": setBoolean(t => t.active),
      "5": setString(t => t.addressRus),
      "6": setString(t => t.addressLat)
    }),
    "15": setBoolean(t => t.incapable),
    "16": setBoolean(t => t.independentChild)
  });
  if (target.applicant.activeFPassports.p2.series) {
    target.applicant.activeFPassports.p1.next = true;
  }
  if (!!target.representative && target.representative !== null) {
    if (!!target.representative.trustRecipient && target.representative.trustRecipient !== null) {
      if (!!target.representative.trustRecipient.personIdentity && target.representative.personIdentity !== null) {
        if (!!target.representative.trustRecipient.personIdentity.fullName && target.representative.personIdentity.fullName !== null) {
          target.representative.trustRecipient.active = !!target.representative.trustRecipient.personIdentity.fullName.lastName;
        }
      }
    }
  }
  if (!!target.secretAccess && !!target.secretAccess.organization) {
    target.secretAccess.value = RestrictionV2.Restricted;
  } else {
    target.secretAccess = new ExtendedRestrictionV2();
    target.secretAccess.value = RestrictionV2.NotRestricted;
  }
  if (!!target.contractObligations && !!target.contractObligations.organization) {
    target.contractObligations.value = RestrictionV2.Restricted;
  } else {
    target.contractObligations = new ExtendedRestrictionV2();
    target.contractObligations.value = RestrictionV2.NotRestricted;
  }
}

function importFromQrCodeToV1(target: RequestFeatures, source: any) {
  if (source['4'] && source['4']['2']) {
    importFrom552QrCodeToV1(target, source);
  } else {
    importFrom60QrCodeToV1(target, source);
  }
}

function importFromQrCodeToV2(target: RequestFeaturesV2, source: any) {
  if (source['4'] && source['4']['2']) {
    importFrom552QrCodeToV2(target, source);
  } else {
    importFrom60QrCodeToV2(target, source);
  }
}

export function importFromQrCode(result: RequestRoot, data: Uint8Array): void {
  const source: any = JSON.parse(new TextDecoder().decode(data));
  if (result.features instanceof RequestFeatures) {
    const features = result.features as RequestFeatures;
    features.ambulanceOptions.departmentLong = result.uid!.substring(0, 9);
    features.stationaryOptions.departmentLong = result.uid!.substring(0, 9);
    features.inputMedia = InputMedia.QrCode;
    features.applicantDate = Features.today(new Date());
    features.receptionDate = Features.today(new Date());
    importFromQrCodeToV1(features, source);
  }
  if (result.features instanceof RequestFeaturesV2) {
    const features = result.features as RequestFeaturesV2;
    if (result.uid) {
      features.ambulanceOptions.departmentLong = result.uid!.substring(0, 9);
      features.stationaryOptions.departmentLong = result.uid!.substring(0, 9);
    }
    features.inputMedia = InputMedia.QrCode;
    features.applicantDate = Features.today(new Date());
    features.receptionDate = Features.today(new Date());
    importFromQrCodeToV2(features, source);
  }
}
